/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Input, message } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn } from '~src/hooks/useStyles';
import { DbFolder } from '~src/types/db';
import { appLog } from '~src/utils/log';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import {
  createGroup,
  RegistrationLabel,
  updateGroup,
} from '~src/services/api/labels';

export interface CreateEditTagModalProps extends Partial<ModalProps> {
  parent?: DbFolder;
  group: RegistrationLabel | null;
  groupsAmount: number;
}

export const CreateEditLabelGroupModal: React.FC<CreateEditTagModalProps> = ({
  parent,
  group,
  groupsAmount,
  onCancel,
  ...modalProps
}) => {
  const intl = useIntl();
  const projectId = useProjectId();
  const [nameVal, setNameVal] = useState(group?.name ?? '');
  const nameInputRef = useRef<Input>(null);

  const archiveSettings = useArchiveSettings();
  const tagQuery = archiveSettings.data?.resTags;

  useEffect(() => {
    setNameVal(group?.name ?? '');
  }, [group]);

  const [doCreateLabelGroup, { isLoading: isLoadingCreate }] = useMutation(
    createGroup,
    {
      onSuccess: (theFolder) => {
        // hide modal
        // @ts-ignore-line
        onCancel && onCancel();

        queryCache.invalidateQueries('registrationLabelGroups');

        // reset values
        setNameVal('');
      },
      onError: (error, item) => {
        message.error(
          intl.formatMessage({ id: 'labels-create-label-group-failed' })
        );
        appLog('CreateEditLabelGroupModal -> createLabelGroup -> item', item);
        appLog('CreateEditLabelGroupModal -> createLabelGroup -> err', error);
      },
    }
  );

  const [doEditLabelGroup, { isLoading: isLoadingEdit }] = useMutation(
    updateGroup,
    {
      onSuccess: (theFolder) => {
        // hide modal
        // @ts-ignore-line
        onCancel && onCancel();

        queryCache.invalidateQueries('registrationLabelGroups');

        // reset values
        setNameVal('');
      },
      onError: (error, item) => {
        message.error(
          intl.formatMessage({ id: 'labels-update-label-group-failed' })
        );
        appLog('CreateEditLabelGroupModal -> editLabelGroup -> item', item);
        appLog('CreateEditLabelGroupModal -> editLabelGroup -> err', error);
      },
    }
  );

  useEffect(() => {
    if (modalProps.visible) {
      setTimeout(() => nameInputRef.current?.focus(), 350);

      // fetch folders if data is older than 1min
      if (Date.now() - archiveSettings.updatedAt > 60 * 1000) {
        archiveSettings.refetch();
      }
    }
  }, [modalProps.visible]);

  const isValid = () => {
    return !!nameVal;
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    if (group?.id) {
      doEditLabelGroup({
        projectId: projectId!,
        groupId: group?.id,
        data: {
          name: nameVal,
          order: group.order,
        },
      });
    } else {
      doCreateLabelGroup({
        projectId: projectId!,
        name: nameVal,
        order: groupsAmount,
      });
    }
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({
            id: group ? 'labels-edit-group-title' : 'labels-create-group-title',
          })}
          icon={
            group
              ? ['far', 'circle-plus']
              : ['far', 'rectangle-history-circle-plus']
          }
          // don't see ['fas', 'rectangle-history-circle-plus']
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{
        disabled: !isValid(),
        loading: isLoadingCreate || isLoadingEdit,
      }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      <Input
        ref={nameInputRef}
        placeholder={intl.formatMessage({ id: 'name' })}
        allowClear
        autoCapitalize="words"
        value={nameVal}
        maxLength={255}
        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
        onChange={({ target: { value } }) =>
          setNameVal(value.charAt(0).toUpperCase() + value.slice(1))
        }
      />
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({});
